import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '@/components/Hero';
import FAQItem from '@/components/FAQItem';
import Testimonials from '@/components/Testimonials';
import FareHarborCalendar from '@/components/FareHarborCalendar';
import { Star, Clock, MapPin, Users } from 'lucide-react';
import StickyBookingButton from '@/components/StickyBookingButton';
import TourStructuredData from '@/components/tours/TourStructuredData';

const FinancialDistrict = () => {
  // Tour features
  const tourHighlights = [
    {
      title: "Wall Street Cats",
      description: "Discover the feline heroes who protected America's financial institutions from rodents and the surprising role they played in economic history."
    },
    {
      title: "City Hall Felines",
      description: "Learn about the cats who made their home in NYC's seat of government and their interactions with famous political figures."
    },
    {
      title: "Postal Service Mousers",
      description: "Explore the story of cats who were officially employed by the U.S. Postal Service to protect the mail and the pneumatic tube system."
    },
    {
      title: "Newspaper Row Cats",
      description: "Hear tales of the printing press cats who kept the news flowing in the golden age of NYC journalism."
    },
    {
      title: "Harbor & Shipping Cats",
      description: "Uncover the adventures of ship cats who protected cargo and accompanied sailors on voyages from the bustling port of New York."
    }
  ];
  
  const testimonials = [
    {
      quote: "The Financial District tour was fascinating! I've walked these streets for years but never knew about the cats who helped build Wall Street. Peggy's knowledge is incredible.",
      author: "Michael T."
    },
    {
      quote: "As a history buff and cat lover, this tour was perfect. The stories about postal cats and City Hall felines were both educational and entertaining. Highly recommend!",
      author: "Sarah J."
    },
    {
      quote: "I never thought I'd be so captivated by stories of cats from 100 years ago! This tour gives you a completely different perspective on New York's financial district.",
      author: "David L."
    }
  ];
  
  const faqs = [
    {
      question: "What should I bring on the tour?",
      answer: "Comfortable walking shoes, a water bottle, and an umbrella if rain is expected. Don't forget your camera to capture the charming sights and stories of our cat-filled adventure."
    },
    {
      question: "Is the tour suitable for children?",
      answer: "Yes, the tour is family-friendly and suitable for children who can walk for about 2 hours and have an interest in cats and history. The stories are engaging for all ages."
    },
    {
      question: "What happens if it rains?",
      answer: "The tour will proceed in light rain, so please bring an umbrella or a raincoat. In case of severe weather, we will reschedule or refund your booking to ensure everyone's safety and comfort."
    },
    {
      question: "How much walking is involved?",
      answer: "The Financial District tour covers approximately 1.5 miles over 2 hours, with several stops along the way. The pace is leisurely, but comfortable walking shoes are recommended."
    },
    {
      question: "Will we see real cats on the tour?",
      answer: "Our tour focuses on historical cat tales rather than live cat sightings. We enjoy the occasional modern feline who might grace us with their presence in a window or doorway, but we can't guarantee any live cat encounters. The magic of our tour is in the historical storytelling that brings these long-ago feline characters to life!"
    },
    {
      question: "Is the tour accessible for people with mobility issues?",
      answer: "The Financial District route is generally accessible, with paved sidewalks and minimal stairs. Please contact us in advance if you have specific mobility concerns, and we'll do our best to accommodate your needs."
    },
    {
      question: "Can I bring my own cat?",
      answer: "Absolutely! Cat-friendly adventurers are welcome to join our tour. We love seeing modern felines exploring the same streets where their historical counterparts once roamed. Just ensure your cat is comfortable with city sounds, crowds, and a 1.5-mile walk. We recommend a secure harness or carrier for their safety. Your cat might even become the star of the tour photos!"
    }
  ];

  // FareHarbor booking link and item ID for Financial District
  const fareHarborItemId = 577800;

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Financial District Historical Cat Tour | Cats About Town</title>
        <meta name="description" content="Discover Wall Street's hidden feline history with our unique Financial District cat walking tour. Book your spot today!" />
        <meta name="keywords" content="Financial District cat tour, Wall Street cats, NYC cat history, feline walking tour" />
        <link rel="canonical" href="https://catsabouttowntours.com/tours/financial-district" />
      </Helmet>
      <TourStructuredData tour={{
        name: 'Financial District Cat Tour',
        description: "Discover Wall Street's hidden feline history with our unique Financial District cat walking tour.",
        image: '/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png',
        url: 'https://catsabouttowntours.com/tours/financial-district',
        price: '40',
        location: 'Financial District'
      }} />
      
      {/* Hero Section with Updated Image */}
      <Hero 
        title="Financial District Historical Cat Tour" 
        subtitle="Discover the historical feline heroes who helped build Wall Street from the 1800s and beyond"
        backgroundImage="/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png"
        height="medium"
        buttonText="Check Availability & Book"
        buttonLink="#book-now"
        showUrgencyMessage={true}
      />
      
      {/* Quick Info Bar */}
      <section className="bg-[#F5EDE0] py-4 border-b border-[#C58B65]/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-[#3E2723]">
            <div className="flex items-center">
              <Clock size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">2 hours</span>
            </div>
            <div className="flex items-center">
              <MapPin size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">1.5 miles of walking</span>
            </div>
            <div className="flex items-center">
              <Users size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">Small groups (max 12 people)</span>
            </div>
            <div className="flex items-center">
              <Star size={18} className="mr-2 text-[#D4AF37]" />
              <span className="text-sm md:text-base">98% 5-star reviews</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tour Overview */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-2">
                <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-6 text-[#3E2723]">
                  Explore Wall Street's Feline History
                </h2>
                <div className="prose prose-lg text-[#3E2723]/90 font-montserrat">
                  <p className="mb-4">
                    Step back in time to discover the surprising role cats played in building America's financial center. This 1.5-mile walking tour through Lower Manhattan reveals the hidden feline history of the Financial District's most iconic landmarks.
                  </p>
                  <p className="mb-4">
                    You'll hear about the cat that saved Wall Street, the feline who christened NYC's pneumatic mail tube system, and the cats who kept watch over millions in gold at the Federal Reserve. From City Hall's famous mousers to the ship cats who protected valuable cargo at the South Street Seaport, these stories bring a fresh perspective to New York's rich history.
                  </p>
                  <p className="mb-4">
                    Curated by Peggy Gavan, author of "The Cat Men of Gotham," and led by our expert licensed guides, this tour combines architectural history, economic insights, and delightful tales of the cats who witnessed it all. Perfect for history buffs, cat lovers, and anyone looking for a unique NYC experience.
                  </p>
                  
                  {/* Added Tour Note */}
                  <div className="bg-[#FAF2E8] p-4 rounded-lg border border-[#C58B65]/20 mb-4">
                    <p className="text-[#8B4513] font-medium">
                      <strong>Tour Note:</strong> Our tours focus on historical stories and not cat-spotting safaris (though if we see a feline friend along the way, we'll consider it a purr-fect bonus!)
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Sticky Booking Card */}
              <div className="md:sticky md:top-24">
                <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/20 shadow-md">
                  <h3 className="font-playfair font-bold text-xl mb-4 text-[#3E2723] text-center">
                    Book Your Spot
                  </h3>
                  <p className="text-center mb-4 text-[#3E2723]/80">
                    $40 per person
                  </p>
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center">
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <span className="ml-2 text-sm text-[#3E2723]/80">
                        (98% 5-star reviews)
                      </span>
                    </div>
                  </div>
                  <a 
                    href="#book-now"
                    className="bg-[#D32F2F] text-white py-3 px-4 rounded font-montserrat font-semibold text-center block hover:bg-[#C62828] transition-colors"
                  >
                    Check Available Dates
                  </a>
                  <p className="text-xs text-center mt-4 text-[#3E2723]/60">
                    Secure booking · Instant confirmation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tour Highlights */}
      <section className="py-12 bg-[#FAF2E8]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
              What You'll Discover
            </h2>
            
            <div className="mb-6">
              <p className="text-[#3E2723]/90 font-montserrat text-center mb-8">
                Our tour brings to life the historical feline residents who played surprising roles in NYC's Financial District. These stories primarily feature cats from the 1800s and early 1900s:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tourHighlights.map((highlight, index) => (
                <div 
                  key={index} 
                  className="bg-white p-6 rounded-lg shadow-sm border border-[#C58B65]/10 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-playfair font-bold text-lg mb-2 text-[#3E2723]">{highlight.title}</h3>
                  <p className="text-[#3E2723]/80 font-montserrat">{highlight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <Testimonials testimonials={testimonials} />
      
      {/* Booking Calendar Section */}
      <section id="book-now" className="py-12 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-4 text-[#3E2723] text-center">
              Book Your Financial District Historical Cat Tour
            </h2>
            <p className="text-center max-w-2xl mx-auto mb-8 text-[#3E2723]/80 font-montserrat">
              Select your preferred date and time below. Tours typically run on weekends and select weekdays.
            </p>
            
            <p className="text-center mb-8 font-montserrat text-[#D32F2F] font-medium">
              Step back in time with our Financial District cat history tour—where Wall Street's feline past comes alive through captivating stories! Feel free to bring your cat companion to experience it with you!
            </p>
            
            <FareHarborCalendar itemId={fareHarborItemId} className="mb-6" />
            
            <p className="text-center text-sm text-[#3E2723]/60 font-montserrat">
              Need help booking? <Link to="/contact" className="text-[#D32F2F] hover:underline">Contact us</Link>
            </p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-12 bg-[#F5EDE0]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem 
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/contact" className="text-[#D32F2F] hover:underline font-montserrat">
                Have another question? Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Other Tours */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
            Explore Our Other Tours
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Brooklyn Heights</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Stroll through America's first suburb and discover its historical feline legends from the 1800s, from historic brownstones to the famous Promenade.
              </p>
              <Link to="/tours/brooklyn-heights" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Tour
              </Link>
            </div>
            
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Lower East Side</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Explore the gritty historical feline tales of NYC's most colorful neighborhood, from 19th century tenement cats to legendary bar mascots of the past.
              </p>
              <Link to="/tours/lower-east-side" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Tour
              </Link>
            </div>
            
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Private Tours</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Create a customized cat history experience for your group, tailored to your interests and schedule.
              </p>
              <Link to="/tours/private-tours" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Options
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mobile sticky booking button */}
      <StickyBookingButton link="#book-now" />
    </div>
  );
};

export default FinancialDistrict;
